/**
 * field-types.ts — SINGLE SOURCE OF TRUTH dla whitelist typów pól.
 *
 * Dwa osobne zestawy celowo:
 *  - SYSTEM (13 tokenów): pola ext modułów systemowych (`<code>_<entity>_ext`).
 *    Mają własny mapper PG (`mapSystemFieldTypeToPgType` w apps/api/lib/ext-fields.ts)
 *    i semantykę fizycznej kolumny w ext-tabeli.
 *  - CUSTOM (48 tokenów): pola modułów własnych (`<code>_module_<slug>`).
 *    Mają własny mapper PG (`mapFieldTypeToPgType` w apps/api/lib/custom-modules/ddl.ts)
 *    i bogatszy zestaw typów (multi-column, arrays, computed…).
 *
 * Pełne mapery PG pozostają po stronie API (Node-only import: drizzle/pg) i NIE trafiają
 * do tej paczki — `@saas/shared` jest importowany też przez Vite/React SPA.
 * Pełna unifikacja zestawów odłożona do T12.S.A.1b (decyzja arch 2026-06-23).
 *
 * NIE importuj tu nic z pg / fs / node:crypto / drizzle.
 */

// ─── System ext field types (12) ─────────────────────────────────────────────

/**
 * 13 tokenów dozwolonych dla pól dodatkowych modułów SYSTEMOWYCH.
 * Podzbiór 48 z FieldRegistry — tylko typy z jednoznacznym mapowaniem PG
 * na kolumnę ext-tabeli (`varchar`, `text`, `integer`, …).
 *
 * `file` — pole DEFINIOWALNE dla referencji pliku (klucz/ścieżka MinIO lub id
 * załącznika). Kolumna PG = TEXT (spójnie z mapperem custom-modules). Pełne
 * wpięcie uploadu do MinIO/`record_attachments` w runtime u tenanta = osobny etap.
 */
export const SYSTEM_EXT_FIELD_TYPES = [
  'varchar',
  'text',
  'integer',
  'numeric',
  'boolean',
  'date',
  'timestamp',
  'select',
  'multiselect',
  'email',
  'url',
  'reference',
  'file',
] as const;

export type SystemExtFieldType = (typeof SYSTEM_EXT_FIELD_TYPES)[number];

/**
 * Set dla `.has()` callsiteów (O(1) lookup).
 * Powielamy zestaw z tablicy — jedno źródło prawdy, dwa formaty.
 */
export const SYSTEM_EXT_FIELD_TYPE_SET: ReadonlySet<string> = new Set(SYSTEM_EXT_FIELD_TYPES);

/**
 * Typy pól dodatkowych, które mogą być „polem daty numeracji" typu dokumentu i typu zadania
 * (rok / miesiąc numeru i klucz resetu licznika liczone z dnia tego pola). Jedno źródło dla
 * walidacji serwera (zapis typu, wyliczenie daty numeru) i list wyboru w edytorach typów.
 */
export const NUMBERING_BASE_DATE_FIELD_TYPES = [
  'date',
  'timestamp',
] as const satisfies readonly SystemExtFieldType[];

export type NumberingBaseDateFieldType = (typeof NUMBERING_BASE_DATE_FIELD_TYPES)[number];

/** Czy pole dodatkowe danego typu może być polem daty numeracji. */
export function isNumberingBaseDateFieldType(
  fieldType: string,
): fieldType is NumberingBaseDateFieldType {
  return NUMBERING_BASE_DATE_FIELD_TYPES.some((type) => type === fieldType);
}

/**
 * Pole rdzenne (systemowe) rekordu, które może być polem daty numeracji typu — wspólny kształt
 * list pól dokumentów (`DocumentCoreDateField`) i zadań (`TaskCoreDateField`) oraz rdzenia
 * wyliczenia daty bazowej po stronie serwera.
 */
export interface NumberingBaseCoreDateField {
  /** Slug pola w katalogu pól kartoteki (np. `issue_date`, `due_at`). */
  readonly slug: string;
  /** Klucz pola w treści żądania zapisu rekordu (camelCase sluga). */
  readonly payloadKey: string;
  /** Klucz i18n etykiety opcji w edytorze typu (listy pól dla panelu go mają). */
  readonly labelKey?: string;
}

// ─── Custom module field types (48) ──────────────────────────────────────────

/**
 * 48 tokenów dozwolonych dla pól modułów WŁASNYCH (SellCall FieldRegistry parytet).
 * F12.A (20 typów MVP) + F12.B batch1 (10) + batch2 (10) + batch3 (8) = 48.
 *
 * UWAGA: `'primaryattachment'` (bez podkreślnika) — kanoniczna nazwa z ddl.ts.
 * Buggy route.ts miał `'primary_attachment'` (z podkreślnikiem) + `'table'`
 * (nie istniejący typ) — oba CELOWO wykluczone z tego zestawu.
 */
export const CUSTOM_FIELD_TYPES = [
  // ── F12.A (20 typów MVP) ────────────────────────────────────────────────────
  // Tekst (5)
  'text',
  'textarea',
  'email',
  'phone',
  'url',
  // Liczby (3)
  'number',
  'decimal',
  'currency',
  // Data (2)
  'date',
  'datetime',
  // Selekcja (4)
  'select',
  'multiselect',
  'checkbox',
  'radio',
  // Media (2)
  'file',
  'image',
  // Relacje (2)
  'relation',
  'user',
  // Specjalne (2)
  'country',
  'autonumber',

  // ── F12.B batch 1 — proste (10 typów) ──────────────────────────────────────
  'toggle',
  'rating',
  'percent',
  'time',
  'daterange',
  'color',
  'slider',
  'hidden',
  'password',
  'tags',

  // ── F12.B batch 2 — średnie (10 typów) ─────────────────────────────────────
  'checkboxgroup',
  'richtext',
  'markdown',
  'code',
  'json',
  'address',
  'barcode',
  'qrcode',
  'icon',
  'primaryattachment',

  // ── F12.B batch 3 — złożone (8 typów) ──────────────────────────────────────
  'signature',
  'gallery',
  'audio',
  'video',
  'location',
  'formula',
  'lookup',
  'rollup',
] as const;

export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

/**
 * Set dla `.has()` callsiteów (O(1) lookup).
 */
export const CUSTOM_FIELD_TYPE_SET: ReadonlySet<string> = new Set(CUSTOM_FIELD_TYPES);

// ─── Multi-column types (shared by both API-side mappers) ────────────────────

/**
 * Typy które generują WIELE kolumn (`<slug>__<subfield>`).
 * Klucz = field_type, wartość = lista `{ suffix, pgType }`.
 *
 * Używane przez mapper w `apps/api/lib/custom-modules/ddl.ts`.
 * Przechowywane tutaj bo ta sama definicja jest potrzebna w kilku
 * route handlerach (records, stats, export, import).
 */
export const MULTI_COLUMN_TYPES: Record<string, Array<{ suffix: string; pgType: string }>> = {
  daterange: [
    { suffix: '__start', pgType: 'DATE' },
    { suffix: '__end', pgType: 'DATE' },
  ],
  address: [
    { suffix: '__street', pgType: 'TEXT' },
    { suffix: '__city', pgType: 'TEXT' },
    { suffix: '__postal', pgType: 'VARCHAR(20)' },
    { suffix: '__country', pgType: 'VARCHAR(100)' },
  ],
  location: [
    { suffix: '__lat', pgType: 'NUMERIC(10,7)' },
    { suffix: '__lng', pgType: 'NUMERIC(10,7)' },
  ],
};
